import type { NavigateFunction } from 'react-router-dom';
import { fetchUserMe } from 'actions/userMe';
import RouteHelpers from 'helpers/routes';
import {
  isSameDomain,
  isSafeRelativePath,
  isValidURLForRedirect
} from 'pages/Users/Login/utils';
import { update } from 'store/slices/user';
import { toastError } from 'components/Toast';

export interface RedirectTargets {
  // Relative path within onelogin (still URL-encoded, as read from a query
  // param or forwarded router state).
  redirectTo?: string | null;
  // Absolute URL to another Teachstone app (still URL-encoded).
  absRedirect?: string | null;
}

interface RedirectDeps {
  dispatch: (action: any) => void;
  navigate: NavigateFunction;
}

// Shared by Login and the expired-password reset flow so the two never
// silently diverge on where an authenticated user ends up. Priority order:
//   1. user.return_to  - oAuth/SSO destination stored in the server session
//   2. redirectTo      - relative path within onelogin (in-app navigation,
//                        except /oauth/ paths, which are Rails routes the SPA
//                        router can't reach and get a full-page redirect)
//   3. absRedirect     - absolute URL to another Teachstone app (full-page)
//   4. the app dashboard (fallback)
// Full-page redirects (1, 3, and the OAuth case in 2) skip fetchUserMe entirely: they tear down the
// SPA and its Redux store, so hydrating it first would just be discarded.
// In-app navigations (2 and 4) fetch first so the destination reads a
// populated store instead of an empty one.
export async function redirectAfterAuth(
  user: any,
  targets: RedirectTargets,
  { dispatch, navigate }: RedirectDeps
): Promise<void> {
  // Defense in depth: return_to should always be a same-app relative path
  // (Devise's request.fullpath always starts with a single "/"), but don't
  // implicitly trust that guarantee holds forever - an unsafe value is
  // simply skipped, falling through to the next priority tier, rather than
  // used as-is.
  if (user.return_to && isSafeRelativePath(user.return_to)) {
    window.location.href = window.location.origin + user.return_to;
    return;
  }

  if (isOAuthPath(targets.redirectTo)) {
    window.location.href = window.location.origin + targets.redirectTo;
    return;
  }

  const redirectTo = targets.redirectTo
    ? decodeURIComponent(targets.redirectTo)
    : null;

  if (redirectTo && isSafeRelativePath(redirectTo)) {
    if (isOAuthPath(redirectTo)) {
      window.location.href = window.location.origin + redirectTo;
      return;
    }

    const meResponse = await fetchUserMe();
    dispatch(update({ ...meResponse.data, skipOnboarding: true }));
    navigate(redirectTo);
    return;
  }

  const absRedirect = targets.absRedirect
    ? decodeURIComponent(targets.absRedirect)
    : null;

  if (
    absRedirect &&
    isValidURLForRedirect(absRedirect) &&
    isSameDomain(absRedirect)
  ) {
    window.location.href = absRedirect;
    return;
  }

  const meResponse = await fetchUserMe();
  dispatch(update(meResponse.data));
  navigate(RouteHelpers.getPath('app'), { replace: true });
}

function isOAuthPath(path?: string | null): path is string {
  return !!path && path.startsWith('/oauth/') && isSafeRelativePath(path);
}

// Reaching here means sign-in already succeeded server-side; only the
// client-side hydration/redirect (e.g. a transient /users/me call) failed.
// Don't surface this as a form error - the user's credentials were fine, and
// on the expired-password form retrying would resubmit a now-stale password.
// A full page load of the app root lets index.tsx's own bootstrap fetch pick
// up the already-authenticated session on a fresh try.
export function handleAuthHydrationFailure(description: string) {
  toastError({ description });
  window.location.href = window.location.origin + RouteHelpers.getPath('app');
}
